/**
 * Mein Konto -- was hinter dem Namen oben rechts steckt.
 *
 * Frueher meldete ein Tipp auf den Namen sofort ab. Das ist an der
 * Einsatzstelle das Schlimmste, was passieren kann: Ohne Netz kommt niemand
 * wieder hinein. Abmelden steht jetzt hier, ausdruecklich und am Ende.
 *
 * Oben die Einstellungen dieses Geraets (gehen immer), darunter das eigene
 * Konto und fuer Administratoren die Verwaltung (gehen nur mit Netz, ADR 0006).
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { GEHEIMNIS_MINDESTLAENGE, geheimnisMangel, type Benutzer } from "@thw/contracts";
import { useEinstellungen, einstellungenSetzen, type Darstellung } from "../api/einstellungen.js";
import { anfrage, sitzungBenutzerAktualisieren, sitzungLesen } from "../api/klient.js";
import { useOnline } from "../api/online.js";
import { abgleichen } from "../lokal/abgleich.js";
import { useSyncZahlen } from "../lokal/bestand.js";
import { Installieren } from "../pwa/installieren.jsx";
import {
  Eingabe,
  Karte,
  Knopf,
  NurMitNetz,
  Rueckmeldung,
  Schalter,
  Wahl,
} from "../ui/bausteine.jsx";

const DARSTELLUNGEN: readonly { wert: Darstellung; text: string }[] = [
  { wert: "automatisch", text: "Wie das Gerät" },
  { wert: "hell", text: "Hell" },
  { wert: "dunkel", text: "Dunkel" },
];

export const ROLLEN_TEXT = { administrator: "Administrator", benutzer: "Benutzer" } as const;

/** Die Regeln aus ADR 0024 in einem Satz -- steht unter jedem Feld fuer ein neues Kennwort. */
export const KENNWORT_REGELN = `Mindestens ${String(GEHEIMNIS_MINDESTLAENGE)} Zeichen, keine Wiederholung wie 111111 oder Folge wie 123456.`;

export function Konto({ abmelden }: { abmelden: () => void }) {
  const sitzung = sitzungLesen();
  const [ich, setIch] = useState<Benutzer | null>(sitzung?.benutzer ?? null);
  const online = useOnline();

  if (ich === null) return null;
  const istAdmin = ich.rolle === "administrator";

  return (
    <div className="flex flex-col gap-4">
      <header className="rounded-xl bg-white p-4 dark:bg-neutral-800">
        <h1 className="text-2xl font-bold">{ich.anzeigename}</h1>
        <p className="text-neutral-600 dark:text-neutral-300">
          {ich.benutzername} · {ROLLEN_TEXT[ich.rolle]}
        </p>
      </header>

      <GeraetEinstellungen />

      <Installieren />

      {!online && <NurMitNetz />}

      <KontoAendern ich={ich} online={online} aufGeaendert={setIch} />

      {/* Das Archiv sieht jeder (ADR 0025), die Pflege nur der Administrator. */}
      <Karte titel="Stammdaten">
        <Link
          to="/archiv"
          className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
        >
          Archiv
        </Link>
        {istAdmin && (
          <>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">
              Nur für Administratoren:
            </p>
            <Link
              to="/verwaltung/lagerorte"
              className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
            >
              Lagerorte
            </Link>
            <Link
              to="/verwaltung/packstuecktypen"
              className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
            >
              Packstücktypen
            </Link>
            <Link
              to="/verwaltung/import"
              className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
            >
              Ausstattungsliste einlesen
            </Link>
            <Link
              to="/verwaltung/status"
              className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
            >
              Status und Farben
            </Link>
          </>
        )}
      </Karte>

      {istAdmin && (
        <Karte titel="Verwaltung">
          <p className="text-sm text-neutral-600 dark:text-neutral-300">Nur für Administratoren.</p>
          <Link
            to="/verwaltung/benutzer"
            className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
          >
            Benutzer und Rollen
          </Link>
        </Karte>
      )}

      <Abmelden abmelden={abmelden} />
    </div>
  );
}

function GeraetEinstellungen() {
  const e = useEinstellungen();
  return (
    <Karte titel="Dieses Gerät">
      <Wahl
        beschriftung="Darstellung"
        wert={e.darstellung}
        moeglichkeiten={DARSTELLUNGEN}
        aendern={(darstellung) => {
          einstellungenSetzen({ darstellung });
        }}
      />
      <Schalter
        beschriftung="Ton nach dem Scan"
        an={e.ton}
        aendern={(ton) => {
          einstellungenSetzen({ ton });
        }}
      />
      <Schalter
        beschriftung="Vibration nach dem Scan"
        an={e.vibration}
        aendern={(vibration) => {
          einstellungenSetzen({ vibration });
        }}
      />
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Gilt für dieses Gerät, egal wer angemeldet ist.
      </p>
      {/* Messung vor dem Einbau in den Scanner (ADR 0033). */}
      <Link
        to="/versuch/texterkennung"
        className="flex min-h-12 items-center rounded-xl bg-neutral-100 px-4 font-semibold active:bg-neutral-200 dark:bg-neutral-700"
      >
        Versuch: Nummer lesen
      </Link>
    </Karte>
  );
}

function KontoAendern({
  ich,
  online,
  aufGeaendert,
}: {
  ich: Benutzer;
  online: boolean;
  aufGeaendert: (b: Benutzer) => void;
}) {
  const [anzeigename, setAnzeigename] = useState(ich.anzeigename);
  const [bisher, setBisher] = useState("");
  const [neu, setNeu] = useState("");
  const [wiederholt, setWiederholt] = useState("");
  const [meldung, setMeldung] = useState<{ art: "gut" | "warnung"; text: string } | null>(null);

  const speichern = useMutation({
    mutationFn: (koerper: Record<string, string>) =>
      anfrage<Benutzer>("PATCH", "/api/core/ich", koerper),
    onSuccess: (benutzer, koerper) => {
      sitzungBenutzerAktualisieren(benutzer);
      aufGeaendert(benutzer);
      // Der neue Name soll auch in der Historie stehen.
      void abgleichen({ stammdaten: true });
      setBisher("");
      setNeu("");
      setWiederholt("");
      setMeldung({
        art: "gut",
        text: "geheimnis_neu" in koerper ? "✓ Kennwort geändert" : "✓ Gespeichert",
      });
    },
    onError: (f) => {
      setMeldung({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });

  const kennwortFehler =
    neu === ""
      ? null
      : (geheimnisMangel(neu, ich.benutzername) ??
        (neu !== wiederholt ? "Die beiden Eingaben unterscheiden sich" : null));

  return (
    <Karte titel="Mein Konto">
      {meldung !== null && <Rueckmeldung art={meldung.art} kopf={meldung.text} />}

      <Eingabe beschriftung="Anzeigename" wert={anzeigename} aendern={setAnzeigename} />
      <Knopf
        disabled={!online || anzeigename.trim() === "" || anzeigename === ich.anzeigename}
        onClick={() => {
          speichern.mutate({ anzeigename: anzeigename.trim() });
        }}
      >
        Namen speichern
      </Knopf>

      <h3 className="mt-2 font-semibold">Kennwort oder PIN ändern</h3>
      <Eingabe
        beschriftung="Bisheriges Kennwort"
        art="password"
        wert={bisher}
        aendern={setBisher}
        autoComplete="current-password"
      />
      <Eingabe
        beschriftung="Neues Kennwort oder PIN"
        art="password"
        wert={neu}
        aendern={setNeu}
        hinweis={`${KENNWORT_REGELN} Eine PIN ist an der Einsatzstelle schneller getippt.`}
        autoComplete="new-password"
      />
      <Eingabe
        beschriftung="Neues Kennwort wiederholen"
        art="password"
        wert={wiederholt}
        aendern={setWiederholt}
        autoComplete="new-password"
      />
      {kennwortFehler !== null && (
        <p className="text-sm font-semibold text-red-700 dark:text-red-300">{kennwortFehler}</p>
      )}
      <Knopf
        disabled={!online || bisher === "" || neu === "" || kennwortFehler !== null}
        onClick={() => {
          speichern.mutate({ geheimnis_bisher: bisher, geheimnis_neu: neu });
        }}
      >
        Kennwort ändern
      </Knopf>
    </Karte>
  );
}

/**
 * Abmelden -- bewusst am Ende und mit Hinweis.
 *
 * Kein Dialog, der eine Entscheidung erzwingt (CLAUDE.md §7). Aber wer noch
 * Buchungen in der Warteschlange hat, soll es sehen: Sie bleiben auf dem
 * Geraet und gehen mit der naechsten Anmeldung hinaus, gleich wessen.
 */
function Abmelden({ abmelden }: { abmelden: () => void }) {
  const wartend = useSyncZahlen()?.wartend ?? 0;

  return (
    <Karte>
      <p className="text-sm text-neutral-600 dark:text-neutral-300">
        Ohne Netz ist keine neue Anmeldung möglich. An der Einsatzstelle angemeldet bleiben.
      </p>
      {wartend > 0 && (
        <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
          Noch {wartend} {wartend === 1 ? "Buchung wartet" : "Buchungen warten"} auf Übertragung.
          Sie bleiben auf dem Gerät und gehen mit der nächsten Anmeldung hinaus.
        </p>
      )}
      <Knopf art="still" onClick={abmelden}>
        Abmelden
      </Knopf>
    </Karte>
  );
}
